// src/pages/bootstrap/BootstrapAdminPage.tsx
// Platform setup surface — guided checklist over the six wizard steps, with
// per-step status, save/validate, and an execution panel with re-run.
// See docs/ux/bootstrap-ia.md. Admin-gated (AdminRoute, in App.tsx).

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useBootstrap } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import {
  ModeStepForm, IdentityStepForm, VaultStepForm, AdminStepForm, ProductsStepForm, SecurityStepForm,
} from '@/features/bootstrap/StepForms';
import { ExecutionPanel } from '@/features/bootstrap/ExecutionPanel';
import { bootstrapStatusVariant, wizardStepVariant, enumSuffix, type StepId } from '@/features/bootstrap/logic';
import type { GetBootstrapStatusResponse, GetSystemInfoResponse, GetWizardStepsResponse } from '@core/types/bootstrap';

function ReadinessRow({ label, ready }: { label: string; ready: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={ready ? 'text-success' : 'text-destructive'}>{ready ? '✓' : '✕'}</span>
    </div>
  );
}

export const BootstrapAdminPage = observer(() => {
  const { t } = useI18n();
  const store = useBootstrap();
  const [status, setStatus] = useState<GetBootstrapStatusResponse | null>(null);
  const [systemInfo, setSystemInfo] = useState<GetSystemInfoResponse | null>(null);
  const [wizard, setWizard] = useState<GetWizardStepsResponse | null>(null);
  const [openStep, setOpenStep] = useState<StepId | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [s, sys, w] = await Promise.all([store.GetStatus(), store.GetSystemInfo(), store.GetWizardSteps()]);
      setStatus(s);
      setSystemInfo(sys);
      setWizard(w);
    } catch (err) {
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  if (isLoading && !wizard) {
    return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  if (loadError) {
    return (
      <div className="p-6">
        <ErrorAlert error={loadError} />
      </div>
    );
  }

  const state = wizard?.state;
  const vaultStatus = systemInfo?.system_info?.vault_status;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('bootstrap.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('bootstrap.subtitle')}</p>
        </div>
        {status?.status && (
          <StatusBadge variant={bootstrapStatusVariant(status.status)} label={t(`bootstrap.status.${enumSuffix(status.status, 'BOOTSTRAP_STATUS_')}`)} />
        )}
      </div>

      {status?.status === 'BOOTSTRAP_STATUS_COMPLETED' && status.redirect_url && (
        <div className="rounded-md border border-success/30 bg-success/5 p-3 text-sm text-foreground">
          {t('bootstrap.completedNote')}{' '}
          <a href={status.redirect_url} className="text-primary hover:underline">{status.redirect_url}</a>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        {t('bootstrap.progress', { done: status?.completed_steps ?? 0, total: status?.total_steps ?? 6 })}
      </p>

      {/* System info */}
      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-2 text-base font-semibold text-foreground">{t('bootstrap.systemInfo.title')}</h2>
        {systemInfo ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <ReadinessRow label={t('bootstrap.systemInfo.database')} ready={systemInfo.system_info?.database_ready ?? false} />
            <ReadinessRow label={t('bootstrap.systemInfo.cache')} ready={systemInfo.system_info?.cache_ready ?? false} />
            <ReadinessRow label={t('bootstrap.systemInfo.broker')} ready={systemInfo.system_info?.broker_ready ?? false} />
            <ReadinessRow label={t('bootstrap.systemInfo.authzEngine')} ready={systemInfo.system_info?.authz_engine_ready ?? false} />
            <ReadinessRow label={t('bootstrap.systemInfo.storage')} ready={systemInfo.system_info?.storage_ready ?? false} />
            {vaultStatus && <ReadinessRow label={t('bootstrap.systemInfo.vault')} ready={vaultStatus.initialized && !vaultStatus.sealed} />}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        )}
        {(systemInfo?.system_info?.missing_services.length ?? 0) > 0 && (
          <p className="mt-2 text-sm text-destructive">
            {t('bootstrap.systemInfo.missing', { services: systemInfo!.system_info!.missing_services.join(', ') })}
          </p>
        )}
      </div>

      {/* Steps checklist */}
      <div className="space-y-2">
        <h2 className="text-base font-semibold text-foreground">{t('bootstrap.steps.title')}</h2>
        {(wizard?.steps ?? []).length === 0 && <p className="text-sm text-muted-foreground">{t('bootstrap.steps.empty')}</p>}
        {(wizard?.steps ?? []).map((step) => {
          const isOpen = openStep === step.id;
          return (
            <div key={step.id} className="rounded-lg border border-border">
              <button
                type="button"
                onClick={() => setOpenStep(isOpen ? null : step.id as StepId)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                aria-expanded={isOpen}
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{step.number}. {step.title}</p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
                <StatusBadge variant={wizardStepVariant(step.status)} label={t(`bootstrap.stepStatus.${enumSuffix(step.status, 'WIZARD_STEP_STATUS_')}`)} size="sm" />
              </button>
              {isOpen && (
                <div className="border-t border-border p-4">
                  {step.id === 'mode' && <ModeStepForm initial={state?.mode} onSaved={load} />}
                  {step.id === 'identity' && <IdentityStepForm initial={state?.identity} onSaved={load} />}
                  {step.id === 'vault' && <VaultStepForm initial={state?.vault} onSaved={load} />}
                  {step.id === 'admin' && <AdminStepForm initial={state?.admin} onSaved={load} />}
                  {step.id === 'products' && <ProductsStepForm initial={state?.products} onSaved={load} />}
                  {step.id === 'security' && <SecurityStepForm initial={state?.security} onSaved={load} />}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ExecutionPanel status={status?.status} onChanged={load} />
    </div>
  );
});

export default BootstrapAdminPage;
